import React, { useState, useId } from 'react';
import './Accordion.less';
import {
  ExpansionPanel,
  ExpansionPanelContent,
} from '@progress/kendo-react-layout';
import { Reveal, Fade } from '@progress/kendo-react-animation';
import { ChevronDownIcon } from 'app/components/icons';
import clsx from 'clsx';
import { useAccordionGroup } from 'app/components/accordions';

interface Props {
  className?: string;
  title: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  children: React.ReactNode;
  isExpanded?: boolean;
  id?: string;
  onToggle?: () => void;
}

export default function Accordion({
  className,
  title,
  subtitle,
  children,
  isExpanded: controlledExpanded,
  id,
  onToggle,
}: Props) {
  const generatedId = useId();
  const panelId = id || generatedId;

  const group = useAccordionGroup();
  const isControlled = controlledExpanded !== undefined;
  const isInGroup = !!group;

  const [internalExpanded, setInternalExpanded] = useState(false);

  const isExpanded = isControlled
    ? controlledExpanded
    : group
    ? group.activeId === panelId
    : internalExpanded;

  const handleToggle = () => {
    if (isControlled && onToggle) {
      onToggle();
    } else if (isInGroup) {
      group!.setActiveId(isExpanded ? null : panelId);
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  return (
    <ExpansionPanel
      className="accordion"
      title={
        <div className="accordion__header" onClick={handleToggle}>
          <div className="accordion__text-block">
            <span className="accordion__title">{title}</span>
            {subtitle && (
              <Fade>
                {!isExpanded && (
                  <span className="accordion__subtitle">{subtitle}</span>
                )}
              </Fade>
            )}
          </div>

          <ChevronDownIcon
            className={clsx('accordion__indicator', {
              accordion__indicator_expanded: isExpanded,
            })}
          />
        </div>
      }
      expanded={isExpanded}
      onAction={handleToggle}
    >
      <Reveal transitionEnterDuration={200} transitionExitDuration={200}>
        {isExpanded && (
          <ExpansionPanelContent>
            <div className={className}>{children}</div>
          </ExpansionPanelContent>
        )}
      </Reveal>
    </ExpansionPanel>
  );
}
